import React from 'react';
import { motion } from 'framer-motion';
import { Mountain, MapPin } from 'lucide-react';

const LANDS = [
  { name: 'Land of Genesis',    threshold: 0,  hint: 'Where every story begins.' },
  { name: 'Patriarch Plains',   threshold: 5,  hint: 'Walk with Abraham, Isaac, Jacob.' },
  { name: 'Sinai Mountains',    threshold: 12, hint: 'Hear the thunder of Torah.' },
  { name: 'Kingdom Heights',    threshold: 22, hint: 'Reign with David in Zion.' },
  { name: "Prophets' Valley",   threshold: 35, hint: 'Whisper of fire and wind.' },
  { name: 'Galilean Shores',    threshold: 50, hint: 'Walk with the Master Himself.' },
  { name: 'Apocalypse Peak',    threshold: 70, hint: 'See the Lamb on His throne.' },
];

// Compute SVG layout — gentle zig-zag path top-to-bottom.
const W = 800;
const STEP_Y = 130;
const POINTS = LANDS.map((_, i) => ({
  x: i % 2 === 0 ? 180 : 620,
  y: 80 + i * STEP_Y,
}));
const HEIGHT = 80 + (LANDS.length - 1) * STEP_Y + 80;

function buildPath() {
  let d = `M ${POINTS[0].x} ${POINTS[0].y}`;
  for (let i = 1; i < POINTS.length; i++) {
    const prev = POINTS[i - 1];
    const cur = POINTS[i];
    const cpY = (prev.y + cur.y) / 2;
    d += ` C ${prev.x} ${cpY}, ${cur.x} ${cpY}, ${cur.x} ${cur.y}`;
  }
  return d;
}

export default function CodexJourney({ state }) {
  const mastered = (state.progress?.completed_words || []).length;
  const avatar = state.progress?.avatar || 'scribe';

  // Find the current "you are here" position — last unlocked land.
  let currentIndex = 0;
  for (let i = 0; i < LANDS.length; i++) {
    if (mastered >= LANDS[i].threshold) currentIndex = i;
  }
  // Progress fraction toward next land (0..1)
  let progressToNext = 1;
  if (currentIndex < LANDS.length - 1) {
    const span = LANDS[currentIndex + 1].threshold - LANDS[currentIndex].threshold;
    const have = mastered - LANDS[currentIndex].threshold;
    progressToNext = Math.max(0, Math.min(1, have / span));
  }

  const avatarPoint = currentIndex < LANDS.length - 1
    ? {
        x: POINTS[currentIndex].x + (POINTS[currentIndex + 1].x - POINTS[currentIndex].x) * progressToNext,
        y: POINTS[currentIndex].y + (POINTS[currentIndex + 1].y - POINTS[currentIndex].y) * progressToNext,
      }
    : POINTS[currentIndex];

  return (
    <div data-testid="codex-journey" style={{ paddingTop: 8 }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 22 }}>
        <div className="codex-eyebrow">YOUR PILGRIMAGE</div>
        <h1 className="codex-h1" style={{ marginTop: 6 }}>The Journey Map</h1>
        <p style={{ opacity: 0.75, marginTop: 6, maxWidth: 580 }}>
          {mastered} word{mastered === 1 ? '' : 's'} mastered. You are walking through{' '}
          <strong style={{ color: 'var(--codex-gold-light)' }}>{LANDS[currentIndex].name}</strong>.
        </p>
      </motion.div>

      <div className="codex-card" style={{ padding: '20px 8px', overflowX: 'auto' }}>
        <svg
          viewBox={`0 0 ${W} ${HEIGHT}`}
          width="100%"
          style={{ maxWidth: '100%', minWidth: 320 }}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id="codex-path-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#d4af37" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#a855f7" stopOpacity="0.7" />
            </linearGradient>
            <radialGradient id="codex-pulse" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Background trail (dashed, full path) */}
          <motion.path
            d={buildPath()}
            stroke="rgba(245, 217, 124, 0.18)"
            strokeWidth={3}
            strokeDasharray="6 8"
            fill="none"
          />

          {/* Walked-so-far solid path (animated) */}
          <motion.path
            d={buildPath()}
            stroke="url(#codex-path-grad)"
            strokeWidth={4}
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: (currentIndex + progressToNext) / (LANDS.length - 1 || 1) }}
            transition={{ duration: 1.6, ease: 'easeInOut' }}
          />

          {/* Land markers */}
          {LANDS.map((land, i) => {
            const unlocked = mastered >= land.threshold;
            const p = POINTS[i];
            return (
              <g key={land.name}>
                <motion.circle
                  cx={p.x}
                  cy={p.y}
                  r={unlocked ? 26 : 20}
                  fill={unlocked ? 'rgba(212,175,55,0.22)' : 'rgba(255,255,255,0.06)'}
                  stroke={unlocked ? '#d4af37' : 'rgba(212,175,55,0.3)'}
                  strokeWidth={unlocked ? 2 : 1}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3 + i * 0.12, type: 'spring', damping: 14 }}
                />
                {unlocked && i === currentIndex && (
                  <motion.circle
                    cx={p.x}
                    cy={p.y}
                    r={36}
                    fill="url(#codex-pulse)"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.6, 0, 0.6] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                  />
                )}
                <text
                  x={p.x}
                  y={p.y + 5}
                  textAnchor="middle"
                  fontSize={unlocked ? 18 : 14}
                  fill={unlocked ? '#fdf6e3' : 'rgba(253,246,227,0.5)'}
                  fontWeight={700}
                  style={{ fontFamily: 'Cinzel, serif' }}
                >
                  {i + 1}
                </text>
                <text
                  x={p.x}
                  y={p.y + 50}
                  textAnchor="middle"
                  fontSize={13}
                  fill={unlocked ? '#f5d97c' : 'rgba(253,246,227,0.4)'}
                  style={{ fontFamily: 'Cinzel, serif', letterSpacing: '0.06em' }}
                >
                  {land.name}
                </text>
                <text
                  x={p.x}
                  y={p.y + 70}
                  textAnchor="middle"
                  fontSize={10}
                  fill="rgba(253,246,227,0.55)"
                >
                  {unlocked ? `${Math.max(0, mastered - land.threshold)} word${(mastered - land.threshold) === 1 ? '' : 's'} since` : `${land.threshold} words to unlock`}
                </text>
              </g>
            );
          })}

          {/* Avatar — animated to current position */}
          <motion.g
            initial={false}
            animate={{ x: avatarPoint.x, y: avatarPoint.y }}
            transition={{ type: 'spring', damping: 18, stiffness: 100, delay: 1.2 }}
          >
            <motion.circle
              r={14}
              fill="#d4af37"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            />
            <foreignObject x={-10} y={-10} width={20} height={20} style={{ overflow: 'visible' }}>
              <div style={{ width: 20, height: 20, color: '#1a0e2c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MapPin size={16} />
              </div>
            </foreignObject>
          </motion.g>
        </svg>
      </div>

      {/* Land details below the map */}
      <div style={{ display: 'grid', gap: 10, marginTop: 22 }}>
        {LANDS.map((land, i) => {
          const unlocked = mastered >= land.threshold;
          const isCurrent = i === currentIndex;
          return (
            <motion.div
              key={land.name}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className={isCurrent ? 'codex-card-gold codex-card' : 'codex-card'}
              style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 14, opacity: unlocked ? 1 : 0.55 }}
              data-testid={`codex-land-${i}`}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: unlocked ? 'linear-gradient(135deg, #d4af37, #ea580c)' : 'rgba(255,255,255,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: unlocked ? '#1a0e2c' : 'rgba(253,246,227,0.5)',
                fontFamily: 'Cinzel, serif', fontWeight: 700,
              }}>
                {i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div className="codex-display" style={{ fontSize: 16, color: 'var(--codex-parchment)' }}>
                  {land.name}
                  {isCurrent && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--codex-gold)', letterSpacing: '0.2em' }}>· YOU ARE HERE</span>}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{land.hint}</div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--codex-gold-light)', textAlign: 'right' }}>
                {unlocked ? <Mountain size={14} style={{ verticalAlign: -2 }} /> : `${land.threshold} words`}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
